import { hasPermission } from '@crm/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, Field, Skeleton } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/select'
import { settingsQuery, type AppSettings } from '@/features/review/api'
import { api, ApiClientError } from '@/lib/api'
import { fetchMe } from '@/lib/auth-query'

export const Route = createFileRoute('/_app/settings')({
  beforeLoad: async ({ context }) => {
    const me = await fetchMe(context.queryClient)
    if (me && !hasPermission(me.user.role, 'settings:manage')) {
      throw redirect({ to: '/' })
    }
  },
  component: SettingsPage,
})

function SettingsPage() {
  const queryClient = useQueryClient()
  const { data, isLoading } = useQuery(settingsQuery)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const save = useMutation({
    mutationFn: (patch: Partial<AppSettings>) =>
      api.put<{ settings: AppSettings }>('/api/settings', patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['settings'] })
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    },
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Could not save'),
  })

  if (isLoading || !data) {
    return (
      <div className="space-y-4 p-8">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  const settings = data.settings

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Settings</h1>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Provider keys are not stored here — they&rsquo;re Worker secrets, set at deploy time.
        </p>
      </div>

      {error && (
        <div
          role="alert"
          className="rounded-md bg-[var(--color-destructive)]/10 px-3 py-2 text-sm text-[var(--color-destructive)]"
        >
          {error}
        </div>
      )}
      {saved && (
        <div className="rounded-md bg-[var(--color-success)]/10 px-3 py-2 text-sm text-[var(--color-success)]">
          Saved.
        </div>
      )}

      <Card className="space-y-4 p-4">
        <h2 className="text-sm font-semibold">Review</h2>

        <label className="flex items-start gap-2 text-sm">
          <Checkbox
            className="mt-0.5"
            checked={settings.autoWelcomeOnConfirm}
            disabled={save.isPending}
            onChange={(e) => {
              setError(null)
              save.mutate({ autoWelcomeOnConfirm: e.target.checked })
            }}
          />
          <span>
            Send a welcome message when a registration is confirmed
            <span className="mt-0.5 block text-xs text-[var(--color-muted-foreground)]">
              Messages start sending once Phase 4 (messaging) is live. Confirming in bulk sends one
              message per client, so this has a real cost.
            </span>
          </span>
        </label>
      </Card>

      <Card className="space-y-4 p-4">
        <h2 className="text-sm font-semibold">Agency</h2>

        <Field
          label="Agency name"
          htmlFor="agencyName"
          hint="Fills the {{agency_name}} placeholder."
        >
          <Input
            id="agencyName"
            defaultValue={settings.agencyName}
            disabled={save.isPending}
            onBlur={(e) => {
              if (e.target.value !== settings.agencyName && e.target.value.trim()) {
                setError(null)
                save.mutate({ agencyName: e.target.value })
              }
            }}
          />
        </Field>

        <Field
          label="Timezone"
          htmlFor="timezone"
          hint="Used for link schedules and the daily birthday send."
        >
          <Input
            id="timezone"
            defaultValue={settings.timezone}
            disabled={save.isPending}
            onBlur={(e) => {
              if (e.target.value !== settings.timezone) {
                setError(null)
                save.mutate({ timezone: e.target.value })
              }
            }}
          />
        </Field>
      </Card>

      <Card className="space-y-4 p-4">
        <h2 className="text-sm font-semibold">Birthday greetings</h2>

        <label className="flex items-start gap-2 text-sm">
          <Checkbox
            className="mt-0.5"
            checked={settings.birthdayEnabled}
            disabled={save.isPending}
            onChange={(e) => {
              setError(null)
              save.mutate({ birthdayEnabled: e.target.checked })
            }}
          />
          <span>
            Send automated birthday greetings
            <span className="mt-0.5 block text-xs text-[var(--color-muted-foreground)]">
              A daily job greets clients whose birthday it is, on the first channel they consent to.
            </span>
          </span>
        </label>

        <Field
          label="Send at (hour, agency time)"
          htmlFor="birthdaySendHour"
          hint="0–23. The job runs hourly and fires once, at this local hour."
        >
          <Input
            id="birthdaySendHour"
            type="number"
            min="0"
            max="23"
            defaultValue={settings.birthdaySendHour}
            disabled={save.isPending}
            onBlur={(e) => {
              const v = Number(e.target.value)
              if (v !== settings.birthdaySendHour && v >= 0 && v <= 23) {
                setError(null)
                save.mutate({ birthdaySendHour: v })
              }
            }}
          />
        </Field>
      </Card>

      <Card className="space-y-4 p-4">
        <h2 className="text-sm font-semibold">Alerts</h2>

        <label className="flex items-start gap-2 text-sm">
          <Checkbox
            className="mt-0.5"
            checked={settings.birthdayAlertsEnabled}
            disabled={save.isPending}
            onChange={(e) => {
              setError(null)
              save.mutate({ birthdayAlertsEnabled: e.target.checked })
            }}
          />
          <span>
            Show birthday alerts on dashboard
            <span className="mt-0.5 block text-xs text-[var(--color-muted-foreground)]">
              Generates alerts for clients whose birthday it is today.
            </span>
          </span>
        </label>

        <label className="flex items-start gap-2 text-sm">
          <Checkbox
            className="mt-0.5"
            checked={settings.linkAlertsEnabled}
            disabled={save.isPending}
            onChange={(e) => {
              setError(null)
              save.mutate({ linkAlertsEnabled: e.target.checked })
            }}
          />
          <span>
            Show link open/close alerts
            <span className="mt-0.5 block text-xs text-[var(--color-muted-foreground)]">
              Alerts when a registration link is about to open or close.
            </span>
          </span>
        </label>

        <label className="flex items-start gap-2 text-sm">
          <Checkbox
            className="mt-0.5"
            checked={settings.linkCapAlertsEnabled}
            disabled={save.isPending}
            onChange={(e) => {
              setError(null)
              save.mutate({ linkCapAlertsEnabled: e.target.checked })
            }}
          />
          <span>
            Show link cap alerts
            <span className="mt-0.5 block text-xs text-[var(--color-muted-foreground)]">
              Alerts when a registration link reaches its maximum registrations.
            </span>
          </span>
        </label>

        <Field
          label="Passport expiry warning (days)"
          htmlFor="passportWarn"
          hint="How many days before passport expiry to alert. 0 = off."
        >
          <Input
            id="passportWarn"
            type="number"
            min="0"
            max="365"
            defaultValue={settings.passportExpiryWarnDays}
            disabled={save.isPending}
            onBlur={(e) => {
              const v = Number(e.target.value)
              if (v !== settings.passportExpiryWarnDays && v >= 0 && v <= 365) {
                setError(null)
                save.mutate({ passportExpiryWarnDays: v })
              }
            }}
          />
        </Field>

        <Field
          label="Review backlog threshold"
          htmlFor="reviewBacklog"
          hint="Alert when pending reviews exceed this count. 0 = off."
        >
          <Input
            id="reviewBacklog"
            type="number"
            min="0"
            defaultValue={settings.reviewBacklogThreshold}
            disabled={save.isPending}
            onBlur={(e) => {
              const v = Number(e.target.value)
              if (v !== settings.reviewBacklogThreshold && v >= 0) {
                setError(null)
                save.mutate({ reviewBacklogThreshold: v })
              }
            }}
          />
        </Field>
      </Card>

      <Card className="space-y-4 p-4">
        <h2 className="text-sm font-semibold">Campaigns &amp; frequency</h2>

        <label className="flex items-start gap-2 text-sm">
          <Checkbox
            className="mt-0.5"
            checked={settings.requireApprovalForStaffCampaigns}
            disabled={save.isPending}
            onChange={(e) => {
              setError(null)
              save.mutate({ requireApprovalForStaffCampaigns: e.target.checked })
            }}
          />
          <span>
            Require admin approval before staff can send a campaign
            <span className="mt-0.5 block text-xs text-[var(--color-muted-foreground)]">
              Staff campaigns are saved as drafts for an admin to send.
            </span>
          </span>
        </label>

        <div className="grid grid-cols-2 gap-4">
          <Field
            label="Marketing message cap"
            htmlFor="freqCount"
            hint="Max marketing messages per client. 0 = no cap."
          >
            <Input
              id="freqCount"
              type="number"
              min="0"
              defaultValue={settings.frequencyCapCount}
              disabled={save.isPending}
              onBlur={(e) => {
                const v = Number(e.target.value)
                if (v !== settings.frequencyCapCount && v >= 0) {
                  setError(null)
                  save.mutate({ frequencyCapCount: v })
                }
              }}
            />
          </Field>
          <Field label="Per (days)" htmlFor="freqDays">
            <Input
              id="freqDays"
              type="number"
              min="1"
              defaultValue={settings.frequencyCapDays}
              disabled={save.isPending}
              onBlur={(e) => {
                const v = Number(e.target.value)
                if (v !== settings.frequencyCapDays && v >= 1) {
                  setError(null)
                  save.mutate({ frequencyCapDays: v })
                }
              }}
            />
          </Field>
        </div>
      </Card>
    </div>
  )
}
